import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Edit3, Sparkles, TrendingUp, Timer } from "lucide-react";

function fmtMxn(n) { return "$" + Number(n || 0).toLocaleString("es-MX"); }

const OFFER_TYPES = [
  { value: "program", label: "Programa" },
  { value: "bundle", label: "Bundle" },
  { value: "subscription", label: "Suscripción" },
  { value: "custom_addon", label: "Add-on custom" },
];

const COPY_VARIANTS = [
  { value: "value", label: "Value · Ahorros" },
  { value: "urgency", label: "Urgency · Countdown" },
  { value: "bonus", label: "Bonus · Regalo" },
];

export default function StaffUpsells() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    try {
      const r = await api.get(`/admin/upsells${q}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/upsells/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!confirm(`¿Desactivar upsell "${u.name}"?`)) return;
    try {
      await api.delete(`/admin/upsells/${u.id}`);
      toast.success("Upsell desactivado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totalRevenue = items.reduce((s, x) => s + (x.revenue_generated_mxn || 0), 0);
  const totalShown = items.reduce((s, x) => s + (x.shown || 0), 0);
  const totalAccepted = items.reduce((s, x) => s + (x.accepted || 0), 0);
  const overallTakeRate = totalShown ? Math.round((totalAccepted / totalShown) * 1000) / 10 : 0;

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">
              Fase 13.4 · Post-purchase
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">
              Upsells one-click
            </h1>
            <p className="mt-1 text-sm text-gray-400">
              Ofertas post-compra que el alumno acepta con un solo click (mock, sin re-cobrar tarjeta).
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            data-testid="admin-upsell-new-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary hover:brightness-110 text-primary-foreground text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4" /> Nuevo upsell
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KpiCard label="Upsells activos" value={items.filter((x) => x.is_active).length} icon={Sparkles} />
          <KpiCard label="Impresiones" value={totalShown.toLocaleString("es-MX")} icon={Timer} />
          <KpiCard label="Take rate" value={`${overallTakeRate}%`} icon={TrendingUp} accent />
          <KpiCard label="Revenue MXN" value={fmtMxn(totalRevenue)} icon={TrendingUp} accent />
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { v: "", l: "Todos" },
            { v: "active", l: "Activos" },
            { v: "inactive", l: "Inactivos" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setStatusFilter(f.v)}
              className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
                statusFilter === f.v
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-gray-800 text-gray-400 hover:bg-white/5"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="upsells-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0F14] text-xs uppercase tracking-widest text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2">Variante</th>
                <th className="text-right px-3 py-2">Precio</th>
                <th className="text-right px-3 py-2">Mostrados</th>
                <th className="text-right px-3 py-2">Aceptados</th>
                <th className="text-right px-3 py-2">Take rate</th>
                <th className="text-right px-3 py-2">Revenue</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-6 text-gray-400">
                    Sin upsells configurados.
                  </td>
                </tr>
              )}
              {items.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-gray-800 hover:bg-white/5"
                  data-testid={`upsell-row-${u.slug}`}
                >
                  <td className="px-3 py-2">
                    <p className="font-semibold text-white">{u.title}</p>
                    <p className="text-xs text-gray-400 font-mono">{u.slug}</p>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <VariantBadge variant={u.copy_variant} />
                    {u.countdown_seconds > 0 && (
                      <span className="ml-1 text-[10px] uppercase tracking-widest text-gray-400">
                        {u.countdown_seconds}s
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-white tabular-nums">
                    {fmtMxn(u.price_mxn)}
                    {u.discount_pct > 0 && (
                      <span className="block text-[10px] text-primary font-bold">
                        {u.discount_pct}% OFF
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-white tabular-nums">
                    {u.shown || 0}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-white tabular-nums">
                    {u.accepted || 0}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-primary font-bold tabular-nums">
                    {u.take_rate_pct || 0}%
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-white tabular-nums">
                    {fmtMxn(u.revenue_generated_mxn || 0)}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive(u)}
                      data-testid={`upsell-toggle-${u.slug}`}
                      className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold border ${
                        u.is_active
                          ? "bg-primary/10 text-primary border-primary/30"
                          : "bg-[#0B0F14] text-gray-400 border-gray-800"
                      }`}
                    >
                      {u.is_active ? "Activo" : "Inactivo"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditItem(u)}
                        data-testid={`upsell-edit-${u.slug}`}
                        aria-label="Editar"
                        className="text-gray-400 hover:text-primary"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(u)}
                        data-testid={`upsell-delete-${u.slug}`}
                        aria-label="Desactivar"
                        className="text-gray-400 hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && (
        <UpsellModal onClose={() => { setShowNew(false); load(); }} initial={null} />
      )}
      {editItem && (
        <UpsellModal
          onClose={() => { setEditItem(null); load(); }}
          initial={editItem}
        />
      )}
    </StaffLayout>
  );
}

function VariantBadge({ variant }) {
  const cfg = {
    urgency: { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/30", label: "URGENCY" },
    value: { bg: "bg-primary/10", text: "text-primary", border: "border-primary/30", label: "VALUE" },
    bonus: { bg: "bg-foreground/10", text: "text-white", border: "border-foreground/30", label: "BONUS" },
  }[variant] || { bg: "bg-[#0B0F14]", text: "text-gray-400", border: "border-gray-800", label: variant };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-widest border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="p-4 rounded-xl border border-gray-800 bg-[#111827]">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 font-semibold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p
        className={`mt-2 text-2xl font-extrabold tabular-nums ${
          accent ? "text-primary" : "text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function UpsellModal({ onClose, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    slug: initial?.slug || "",
    title: initial?.title || "",
    headline: initial?.headline || "",
    description: initial?.description || "",
    offer_type: initial?.offer_type || "program",
    price_mxn: initial?.price_mxn || 0,
    original_price_mxn: initial?.original_price_mxn || 0,
    discount_pct: initial?.discount_pct || 0,
    copy_variant: initial?.copy_variant || "value",
    countdown_seconds: initial?.countdown_seconds || 0,
    is_active: initial?.is_active ?? true,
    priority: initial?.priority || 0,
    trigger_rules: {
      tenant_scope: initial?.trigger_rules?.tenant_scope || "global",
      after_program_slugs: (initial?.trigger_rules?.after_program_slugs || []).join(", "),
      min_order_total_mxn: initial?.trigger_rules?.min_order_total_mxn || 0,
    },
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        title: form.title,
        headline: form.headline,
        description: form.description,
        offer_type: form.offer_type,
        price_mxn: Number(form.price_mxn) || 0,
        original_price_mxn: Number(form.original_price_mxn) || 0,
        discount_pct: Number(form.discount_pct) || 0,
        copy_variant: form.copy_variant,
        countdown_seconds: Number(form.countdown_seconds) || 0,
        is_active: form.is_active,
        priority: Number(form.priority) || 0,
        trigger_rules: {
          tenant_scope: form.trigger_rules.tenant_scope,
          after_program_slugs: form.trigger_rules.after_program_slugs
            .split(",").map((s) => s.trim()).filter(Boolean),
          min_order_total_mxn: Number(form.trigger_rules.min_order_total_mxn) || 0,
        },
      };
      if (isEdit) {
        await api.patch(`/admin/upsells/${initial.id}`, payload);
        toast.success("Upsell actualizado");
      } else {
        await api.post("/admin/upsells", payload);
        toast.success("Upsell creado");
      }
      onClose();
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="upsell-modal"
        className="w-full max-w-2xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3 my-8"
      >
        <div className="flex justify-between">
          <h3 className="text-xl font-bold text-white font-display">
            {isEdit ? "Editar upsell" : "Nuevo upsell one-click"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-gray-400 hover:text-primary"
          >
            <X />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <F label="Nombre interno *" val={form.name} on={(v) => setForm({ ...form, name: v })} testid="um-name" required />
          <F label="Slug (auto si vacío)" val={form.slug} on={(v) => setForm({ ...form, slug: v })} testid="um-slug" />
          <F label="Título público *" val={form.title} on={(v) => setForm({ ...form, title: v })} testid="um-title" required span="md:col-span-2" />
          <F label="Headline (eyebrow)" val={form.headline} on={(v) => setForm({ ...form, headline: v })} testid="um-headline" span="md:col-span-2" />
        </div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Descripción</span>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="um-description"
            rows={2}
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Tipo</label>
            <select
              value={form.offer_type}
              onChange={(e) => setForm({ ...form, offer_type: e.target.value })}
              data-testid="um-offer-type"
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
            >
              {OFFER_TYPES.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>
          <F label="Precio MXN *" type="number" val={form.price_mxn} on={(v) => setForm({ ...form, price_mxn: v })} testid="um-price" required />
          <F label="Precio antes (tachado)" type="number" val={form.original_price_mxn} on={(v) => setForm({ ...form, original_price_mxn: v })} testid="um-original" />
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Copy variant</label>
            <select
              value={form.copy_variant}
              onChange={(e) => setForm({ ...form, copy_variant: e.target.value })}
              data-testid="um-copy-variant"
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
            >
              {COPY_VARIANTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>
          <F label="Descuento %" type="number" val={form.discount_pct} on={(v) => setForm({ ...form, discount_pct: v })} testid="um-discount" />
          <F label="Countdown seg (≤900)" type="number" val={form.countdown_seconds} on={(v) => setForm({ ...form, countdown_seconds: v })} testid="um-countdown" />
        </div>

        <div className="pt-3 border-t border-gray-800">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">
            Reglas de disparo
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            <F
              label="Tenant scope"
              val={form.trigger_rules.tenant_scope}
              on={(v) => setForm({ ...form, trigger_rules: { ...form.trigger_rules, tenant_scope: v } })}
              testid="um-scope"
              placeholder="global | dcc | kapital"
            />
            <F
              label="Después de programas (slugs, coma)"
              val={form.trigger_rules.after_program_slugs}
              on={(v) => setForm({ ...form, trigger_rules: { ...form.trigger_rules, after_program_slugs: v } })}
              testid="um-after-programs"
              placeholder="riesgo-corporativo,basilea-iv"
            />
            <F
              label="Mínimo orden MXN"
              type="number"
              val={form.trigger_rules.min_order_total_mxn}
              on={(v) => setForm({ ...form, trigger_rules: { ...form.trigger_rules, min_order_total_mxn: v } })}
              testid="um-min-total"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-gray-800">
          <F label="Prioridad (mayor=primero)" type="number" val={form.priority} on={(v) => setForm({ ...form, priority: v })} testid="um-priority" />
          <label className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="um-active"
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm text-white font-semibold">Activo</span>
          </label>
        </div>

        <div className="flex gap-2 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-md border border-gray-800 text-white hover:bg-white/5 font-semibold text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="um-submit"
            className="flex-1 py-2.5 rounded-md bg-primary hover:brightness-110 text-primary-foreground font-semibold text-sm disabled:opacity-60"
          >
            {submitting ? "…" : isEdit ? "Guardar cambios" : "Crear upsell"}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, val, on, testid, required, type = "text", span = "", placeholder = "" }) {
  return (
    <label className={`block ${span}`}>
      <span className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">{label}</span>
      <input
        value={val}
        onChange={(e) => on(e.target.value)}
        required={required}
        type={type}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
    </label>
  );
}
